import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { cn } from '@repo/ui/lib/utils';
import type { ReactNode } from 'react';
import { WizardProgressBar } from '@/components/report/wizard-progress-bar';

type ReportWizardShellProps = {
  /** Short label above the title (e.g. “KYC report” or “Create a report”). */
  topLeftLabel: string;
  formStepIndex: number;
  formStepTotal: number;
  title: string;
  description: string;
  children: ReactNode;
  sidebar: ReactNode;
  className?: string;
};

/**
 * Shared two-column shell for report creation (type picker + wizard) so layout matches end-to-end.
 */
export function ReportWizardShell({
  topLeftLabel,
  formStepIndex,
  formStepTotal,
  title,
  description,
  children,
  sidebar,
  className,
}: ReportWizardShellProps) {
  useLocale();

  const currentIndex = Math.max(0, formStepIndex - 1);

  return (
    <div className={cn('flex h-full flex-col overflow-hidden', className)}>
      <div className="flex flex-1 gap-0 overflow-hidden">
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden border-e">
          <div className="shrink-0 space-y-3 border-b px-6 pt-6 pb-4">
            <div className="flex items-center gap-2">
              <span className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                {topLeftLabel}
              </span>
              <span className="text-muted-foreground text-xs">·</span>
              <span className="text-muted-foreground text-xs">
                {t('reports.step')} {formStepIndex} {t('reports.of')} {formStepTotal}
              </span>
            </div>
            <h2 className="font-semibold text-lg tracking-tight">{title}</h2>
            <p className="text-muted-foreground text-sm">{description}</p>
            <WizardProgressBar
              className="mt-1"
              currentIndex={currentIndex}
              totalSteps={formStepTotal}
            />
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6">{children}</div>
        </div>

        <div className="flex w-72 shrink-0 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-4 py-6">{sidebar}</div>
        </div>
      </div>
    </div>
  );
}
